import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Primitives";
import { toast } from "sonner";
import { Sparkle, Plus, Trash, FileArrowDown, MicrosoftExcelLogo } from "@phosphor-icons/react";
import {
  parseQuestionExcelFile,
  rowsToQuestionPayloads,
  downloadQuestionExcelTemplate,
} from "@/lib/questionExcelImport";

function toggleId(list, id) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export default function TeacherQuestions() {
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [filter, setFilter] = useState({ subject_id: "", chapter_id: "", type: "" });
  const [form, setForm] = useState({ type: "mcq", text: "", options: ["", "", "", ""], correct_index: 0, marks: 1, difficulty: "medium" });
  const [ai, setAi] = useState({ type: "mcq", count: 5, difficulty: "medium", topic_hint: "" });
  const [gen, setGen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const importFileRef = useRef(null);
  const fetchSeqRef = useRef(0);

  useEffect(() => { api.get("/subjects").then(r => setSubjects(r.data)); }, []);

  /** Load questions for current filter; ignore stale responses from overlapping requests. */
  const loadQuestions = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    if (!filter.subject_id) {
      setQuestions([]);
      setSelectedIds([]);
      return;
    }
    const p = new URLSearchParams();
    p.set("subject_id", filter.subject_id);
    if (filter.chapter_id) p.set("chapter_id", filter.chapter_id);
    if (filter.type) p.set("type", filter.type);
    try {
      const { data } = await api.get(`/questions?${p.toString()}`);
      if (seq !== fetchSeqRef.current) return;
      setQuestions(Array.isArray(data) ? data : []);
      setSelectedIds([]);
    } catch (err) {
      if (seq === fetchSeqRef.current) {
        toast.error(err?.response?.data?.detail || "Failed to load questions");
      }
    }
  }, [filter]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const selectedSubject = subjects.find(s => s.id === filter.subject_id);
  const chapters = selectedSubject?.chapters || [];

  const addQ = async (e) => {
    e.preventDefault();
    if (!filter.subject_id || !filter.chapter_id) { toast.error("Select subject and chapter first"); return; }
    const payload = {
      subject_id: filter.subject_id,
      chapter_id: filter.chapter_id,
      type: form.type,
      text: form.text,
      marks: Number(form.marks),
      difficulty: form.difficulty,
      options: form.type === "mcq" ? form.options : null,
      correct_index: form.type === "mcq" ? Number(form.correct_index) : null,
    };
    await api.post("/questions", payload);
    toast.success("Question added");
    setForm({ ...form, text: "", options: ["", "", "", ""] });
    await loadQuestions();
  };

  const aiGen = async () => {
    if (!filter.subject_id || !filter.chapter_id) { toast.error("Select subject and chapter"); return; }
    setGen(true);
    try {
      const { data } = await api.post("/questions/ai-generate", {
        subject_id: filter.subject_id,
        chapter_id: filter.chapter_id,
        type: ai.type,
        count: Number(ai.count),
        difficulty: ai.difficulty,
        topic_hint: ai.topic_hint || null,
        auto_save: true,
      });
      toast.success(`Generated ${data.questions.length} questions`);
      await loadQuestions();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setGen(false); }
  };

  const allSelected = questions.length > 0 && selectedIds.length === questions.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : questions.map((q) => q.id));
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!confirm(`Delete ${selectedIds.length} selected question(s)?`)) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.post("/questions/bulk-delete", { question_ids: selectedIds });
      const n = data.deleted ?? selectedIds.length;
      toast.success(`Deleted ${n} question(s)`);
      setSelectedIds([]);
      await loadQuestions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete questions");
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!filter.subject_id || !filter.chapter_id) {
      toast.error("Select subject and chapter in the filter first.");
      return;
    }
    setImportBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const rows = parseQuestionExcelFile(buf);
      const { payloads, errors } = rowsToQuestionPayloads(filter.subject_id, filter.chapter_id, rows);
      if (!payloads.length) {
        const msg = errors.length ? errors.map((x) => `Row ${x.row}: ${x.message}`).join("; ") : "No valid rows found.";
        toast.error(msg);
        return;
      }
      const { data } = await api.post("/questions/bulk", { questions: payloads });
      const n = data.count ?? data.questions?.length ?? 0;
      if (errors.length) {
        const detail = errors.slice(0, 3).map((x) => `Row ${x.row}: ${x.message}`).join(" · ");
        const more = errors.length > 3 ? ` (+${errors.length - 3} more)` : "";
        toast.warning(`Imported ${n} question(s). Skipped ${errors.length} row(s): ${detail}${more}`);
      } else {
        toast.success(`Imported ${n} question(s).`);
      }
      await loadQuestions();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err?.message || "Import failed.");
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Repository" title="Question bank" subtitle="MCQs & descriptive questions, chapter-wise." />

      <Section title="Filter">
        <div className="card-academic p-6 grid md:grid-cols-3 gap-4">
          <Select label="Subject" value={filter.subject_id} onChange={v => setFilter({ ...filter, subject_id: v, chapter_id: "" })}
            options={[{ value: "", label: "— Select —" }, ...subjects.map(s => ({ value: s.id, label: `${s.name} · ${s.class_group}` }))]} testid="f-subject" />
          <Select label="Chapter" value={filter.chapter_id} onChange={v => setFilter({ ...filter, chapter_id: v })}
            options={[{ value: "", label: "All chapters" }, ...chapters.map(c => ({ value: c.id, label: c.name }))]} testid="f-chapter" />
          <Select label="Type" value={filter.type} onChange={v => setFilter({ ...filter, type: v })}
            options={[{ value: "", label: "All" }, { value: "mcq", label: "MCQ" }, { value: "descriptive", label: "Descriptive" }]} testid="f-type" />
        </div>
      </Section>

      <Section title="AI-assisted generation" right={<span className="overline">DeepSeek V4 Flash (free)</span>}>
        <div className="card-academic p-6 grid md:grid-cols-5 gap-4 items-end">
          <Select label="Type" value={ai.type} onChange={v => setAi({ ...ai, type: v })}
            options={[{ value: "mcq", label: "MCQ" }, { value: "descriptive", label: "Descriptive" }]} testid="ai-type" />
          <div>
            <label className="overline block mb-2">Count</label>
            <input type="number" min={1} max={20} value={ai.count} onChange={e => setAi({ ...ai, count: e.target.value })} data-testid="ai-count"
              className="w-full bg-ivory border border-stone px-4 py-3" />
          </div>
          <Select label="Difficulty" value={ai.difficulty} onChange={v => setAi({ ...ai, difficulty: v })}
            options={[{ value: "easy", label: "Easy" }, { value: "medium", label: "Medium" }, { value: "hard", label: "Hard" }]} testid="ai-diff" />
          <div className="md:col-span-2">
            <label className="overline block mb-2">Topic hint (optional)</label>
            <input value={ai.topic_hint} onChange={e => setAi({ ...ai, topic_hint: e.target.value })} data-testid="ai-hint"
              className="w-full bg-ivory border border-stone px-4 py-3" placeholder="e.g., focus on quadratic formula" />
          </div>
          <button onClick={aiGen} disabled={gen} className="btn-primary md:col-span-5 flex items-center gap-2 justify-center" data-testid="ai-generate">
            <Sparkle size={16} weight={gen ? "fill" : "regular"} /> {gen ? "Generating…" : "Generate with AI"}
          </button>
        </div>
      </Section>

      <Section title="Add question manually">
        <form onSubmit={addQ} className="card-academic p-6 grid md:grid-cols-2 gap-4">
          <Select label="Type" value={form.type} onChange={v => setForm({ ...form, type: v })}
            options={[{ value: "mcq", label: "MCQ" }, { value: "descriptive", label: "Descriptive" }]} testid="q-type" />
          <div>
            <label className="overline block mb-2">Marks</label>
            <input type="number" min={1} value={form.marks} onChange={e => setForm({ ...form, marks: e.target.value })} data-testid="q-marks"
              className="w-full bg-ivory border border-stone px-4 py-3" />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Question</label>
            <textarea required rows={3} value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} data-testid="q-text"
              className="w-full bg-ivory border border-stone px-4 py-3" />
          </div>
          {form.type === "mcq" && form.options.map((o, i) => (
            <div key={i}>
              <label className="overline block mb-2 flex items-center gap-2">
                <input type="radio" name="correct" checked={Number(form.correct_index) === i} onChange={() => setForm({ ...form, correct_index: i })} data-testid={`q-correct-${i}`} />
                Option {String.fromCharCode(65 + i)} {Number(form.correct_index) === i && <span className="text-terracotta">(correct)</span>}
              </label>
              <input required value={o} onChange={e => {
                const opts = [...form.options]; opts[i] = e.target.value; setForm({ ...form, options: opts });
              }} data-testid={`q-opt-${i}`} className="w-full bg-ivory border border-stone px-4 py-3" />
            </div>
          ))}
          <button className="btn-primary md:col-span-2 flex items-center gap-2 justify-center" data-testid="q-submit"><Plus size={16} /> Add question</button>
        </form>
      </Section>

      <Section title="Import from Excel">
        <div className="card-academic p-6 space-y-4">
          <p className="text-sm text-mossy max-w-3xl">
            Use the same subject and chapter as in the filter above. First row must be headers:&nbsp;
            <span className="font-mono text-ink">
              Type · Marks · Question · Option A · Option B · Option C · Option D · Correct
            </span>
            . For <strong>MCQ</strong>, fill options and set <strong>Correct</strong> to <span className="font-mono">A</span>
            –<span className="font-mono">D</span> (or <span className="font-mono">1</span>–<span className="font-mono">4</span>). For{" "}
            <strong>Descriptive</strong>, set Type to <span className="font-mono">descriptive</span>
            &nbsp;and leave options blank.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <button
              type="button"
              onClick={() => downloadQuestionExcelTemplate()}
              className="btn-secondary inline-flex items-center gap-2 px-4 py-3"
              data-testid="excel-template"
            >
              <FileArrowDown size={18} /> Download template
            </button>
            <input
              ref={importFileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="excel-file"
              onChange={handleExcelImport}
            />
            <button
              type="button"
              disabled={importBusy}
              onClick={() => importFileRef.current?.click()}
              className="btn-primary inline-flex items-center gap-2 px-4 py-3"
              data-testid="excel-import"
            >
              <MicrosoftExcelLogo size={18} /> {importBusy ? "Importing…" : "Choose Excel file"}
            </button>
          </div>
        </div>
      </Section>

      <Section
        title={`Questions (${questions.length})`}
        right={questions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-mossy cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleSelectAll}
                data-testid="select-all-questions"
                className="h-4 w-4 accent-terracotta"
              />
              Select all
            </label>
            <button
              type="button"
              onClick={bulkDelete}
              disabled={!selectedIds.length || deleteBusy}
              className="btn-secondary inline-flex items-center gap-2 text-sm px-4 py-2 disabled:opacity-50"
              data-testid="bulk-delete-questions"
            >
              <Trash size={16} />
              {deleteBusy ? "Deleting…" : `Delete selected (${selectedIds.length})`}
            </button>
          </div>
        ) : null}
      >
        {!filter.subject_id ? (
          <EmptyState title="Select a subject" body="Choose a subject (and chapter) in the filter above to view and manage questions." />
        ) : questions.length === 0 ? (
          <EmptyState title="No questions yet" body="Add questions manually, import from Excel, or generate with AI for this subject and chapter." />
        ) : (
          <div className="space-y-3">
            {questions.map((q, i) => (
              <div
                key={q.id}
                className={`card-academic p-5 ${selectedIds.includes(q.id) ? "border-forest bg-sand/40" : ""}`}
              >
                <div className="flex items-start gap-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(q.id)}
                    onChange={() => setSelectedIds((prev) => toggleId(prev, q.id))}
                    data-testid={`select-q-${q.id}`}
                    className="mt-1 h-4 w-4 shrink-0 accent-terracotta"
                    aria-label={`Select question ${i + 1}`}
                  />
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <span className="overline">#{String(i + 1).padStart(2, "0")}</span>
                      <span className="overline">{(q.type || "unknown").toUpperCase()}</span>
                      <span className="overline text-terracotta">{q.difficulty ?? "—"}</span>
                      <span className="overline font-mono">{q.marks} marks</span>
                      {q.ai_generated && <span className="overline flex items-center gap-1"><Sparkle size={10} /> AI</span>}
                    </div>
                    <div className="text-ink mb-3">{q.text}</div>
                    {q.options && (
                      <ol className="space-y-1 text-sm">
                        {q.options.map((o, oi) => (
                          <li key={oi} className={oi === q.correct_index ? "text-forest font-medium" : "text-mossy"}>
                            <span className="font-mono mr-2">{String.fromCharCode(65 + oi)}.</span>{o}
                            {oi === q.correct_index && <span className="ml-2 text-terracotta text-xs">✓ correct</span>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-ivory border border-stone px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
